import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import StandardModal from 'modals/StandardModal';
import Spinner from 'components/Spinner';
import Alert from 'view/components/Alert';
import { FormInput, FormSelect, FormAjaxSelect } from 'view/components/Form';
import { closeModal } from 'data/reducers/modalReducer';
import { showToast } from 'data/reducers/toastReducer';
import { adminUsersThunks } from 'data/repositories/adminUsersRepository';
import { useSubscriptionPlans } from 'data/repositories/subscriptionPlansRepository';
import { useAdminSubscriptions } from 'data/repositories/adminSubscriptionsRepository';
import { SUBSCRIPTION_PLANS } from 'constants/subscriptions';

const SubscriptionCreateModal = ({ options }) => {
	const dispatch = useDispatch();
	const { onSuccess } = options || {};

	// Get plans and subscription actions from repositories
	const {
		items: plans,
		isLoading: isLoadingPlans,
		actions: planActions,
	} = useSubscriptionPlans();

	const { actions: subscriptionActions, createError } = useAdminSubscriptions();

	// React Hook Form setup
	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { isSubmitting },
	} = useForm({
		defaultValues: {
			user_id: '',
			plan_id: '',
			property_limit: '',
			seat_limit: '',
			unlimited_properties: false,
			unlimited_seats: false,
			price: '',
			contract_start_date: '',
			contract_end_date: '',
			notes: '',
			lock_property_management: true,
		},
		mode: 'onChange',
	});

	// Watch form values for conditional logic
	const watchUnlimitedProperties = watch('unlimited_properties');
	const watchUnlimitedSeats = watch('unlimited_seats');
	const watchPlanId = watch('plan_id');

	// Fetch Enterprise plans on mount (users will be loaded on demand via AJAX)
	useEffect(() => {
		planActions.fetchAll({ isEnterprise: true });
	}, [planActions]);

	// Auto-select Enterprise plan when plans load
	useEffect(() => {
		if (plans.length > 0 && !watchPlanId) {
			const enterprisePlan = plans.find(
				(plan) => plan.slug === SUBSCRIPTION_PLANS.ENTERPRISE
			);
			if (enterprisePlan) {
				setValue('plan_id', enterprisePlan.id.toString());
			}
		}
	}, [plans, watchPlanId, setValue]);

	// Form submission handler
	const onSubmit = async (data) => {
		try {
			const payload = {
				user_id: parseInt(data.user_id),
				property_limit: data.unlimited_properties
					? -1
					: parseInt(data.property_limit),
				seat_limit: data.unlimited_seats ? -1 : parseInt(data.seat_limit),
				price: data.price ? parseInt(data.price) : null,
				contract_start_date: data.contract_start_date || null,
				contract_end_date: data.contract_end_date || null,
				notes: data.notes || null,
				lock_property_management: data.lock_property_management,
			};

			const response = await subscriptionActions.createEnterprise({
				data: payload,
			});

			if (response?.success) {
				dispatch(
					showToast({
						type: 'success',
						title: 'Success!',
						body: 'Enterprise subscription created successfully',
						duration: 4000,
					})
				);

				// Call onSuccess callback to refresh the list
				if (onSuccess) {
					onSuccess();
				}

				dispatch(closeModal());
			}
		} catch (err) {}
	};

	const handleClose = () => {
		dispatch(closeModal());
	};

	const selectedPlan = plans.find((p) => p.id === parseInt(watchPlanId));
	const isEnterprisePlanSelected =
		selectedPlan?.slug === SUBSCRIPTION_PLANS.ENTERPRISE;
	const isLoadingData = isLoadingPlans;

	// Filter to show only Enterprise plan
	const enterprisePlans = plans.filter(
		(plan) => plan.slug === SUBSCRIPTION_PLANS.ENTERPRISE
	);

	return (
		<StandardModal
			title="Create Enterprise Subscription"
			onClose={handleClose}
			size="lg"
			noFooter
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				{isLoadingData ? (
					<div className="text-center py-4">
						<Spinner />
						<p className="mt-3 text-muted">Loading data...</p>
					</div>
				) : (
					<>
						{createError && (
							<Alert type="danger" className="mb-3">
								{typeof createError === 'string'
									? createError
									: createError.message ||
									  createError.data?.message ||
									  'Failed to create subscription. Please try again.'}
							</Alert>
						)}

						{/* User Selection (AJAX-backed) */}
						<FormAjaxSelect
							name="user_id"
							control={control}
							label="Select User"
							callbackFunc={(query) =>
								adminUsersThunks.fetchPaginated(1, 20, { search: query })
							}
							valueAccessor={(user) => user.id}
							labelAccessor={(user) => `${user.name} (${user.email})`}
							placeholder="-- Search and select a user --"
							required
							disabled={isSubmitting}
						/>

						{/* Plan Selection */}
						<FormSelect
							name="plan_id"
							control={control}
							label="Select Plan"
							options={enterprisePlans}
							optionValue="id"
							optionLabel="name"
							placeholder="-- Select a plan --"
							required
							disabled={isSubmitting}
							helpText="Currently only Enterprise plans can be manually created"
						/>

						{/* Enterprise Plan Fields */}
						{isEnterprisePlanSelected && (
							<>
								{/* Property Limit with Unlimited Checkbox */}
								<div className="mb-3">
									<label className="form-label">
										Property Limit <span className="text-danger">*</span>
									</label>
									<div className="input-group">
										<Controller
											name="property_limit"
											control={control}
											rules={{
												required:
													!watchUnlimitedProperties &&
													'Property limit is required',
												min: {
													value: 0,
													message: 'Property limit must be at least 0',
												},
											}}
											render={({ field, fieldState: { error } }) => (
												<>
													<input
														{...field}
														type="number"
														className={`form-control ${
															error ? 'is-invalid' : ''
														}`}
														placeholder="Enter number of properties"
														disabled={isSubmitting || watchUnlimitedProperties}
														min="0"
													/>
													{error && (
														<div className="invalid-feedback">
															{error.message}
														</div>
													)}
												</>
											)}
										/>
										<div className="input-group-text">
											<Controller
												name="unlimited_properties"
												control={control}
												render={({ field }) => (
													<>
														<input
															type="checkbox"
															className="form-check-input mt-0"
															id="unlimited-properties"
															checked={field.value}
															onChange={(e) => {
																field.onChange(e.target.checked);
																if (e.target.checked) {
																	setValue('property_limit', '');
																}
															}}
															disabled={isSubmitting}
														/>
														<label
															className="ms-2 mb-0"
															htmlFor="unlimited-properties"
														>
															Unlimited
														</label>
													</>
												)}
											/>
										</div>
									</div>
								</div>

								{/* Seat Limit with Unlimited Checkbox */}
								<div className="mb-3">
									<label className="form-label">
										Seat Limit <span className="text-danger">*</span>
									</label>
									<div className="input-group">
										<Controller
											name="seat_limit"
											control={control}
											rules={{
												required:
													!watchUnlimitedSeats && 'Seat limit is required',
												min: {
													value: 0,
													message: 'Seat limit must be at least 0',
												},
											}}
											render={({ field, fieldState: { error } }) => (
												<>
													<input
														{...field}
														type="number"
														className={`form-control ${
															error ? 'is-invalid' : ''
														}`}
														placeholder="Enter number of seats"
														disabled={isSubmitting || watchUnlimitedSeats}
														min="0"
													/>
													{error && (
														<div className="invalid-feedback">
															{error.message}
														</div>
													)}
												</>
											)}
										/>
										<div className="input-group-text">
											<Controller
												name="unlimited_seats"
												control={control}
												render={({ field }) => (
													<>
														<input
															type="checkbox"
															className="form-check-input mt-0"
															id="unlimited-seats"
															checked={field.value}
															onChange={(e) => {
																field.onChange(e.target.checked);
																if (e.target.checked) {
																	setValue('seat_limit', '');
																}
															}}
															disabled={isSubmitting}
														/>
														<label
															className="ms-2 mb-0"
															htmlFor="unlimited-seats"
														>
															Unlimited
														</label>
													</>
												)}
											/>
										</div>
									</div>
								</div>

								{/* Price in Cents */}
								<FormInput
									name="price"
									control={control}
									label="Price"
									type="number"
									placeholder="$99.99"
									disabled={isSubmitting}
									rules={{
										min: {
											value: 0,
											message: 'Price must be 0 or greater',
										},
									}}
									helpText="Custom price for this enterprise subscription (optional)"
								/>

								{/* Contract Dates */}
								<div className="row">
									<div className="col-md-6">
										<FormInput
											name="contract_start_date"
											control={control}
											label="Contract Start Date"
											type="date"
											disabled={isSubmitting}
										/>
									</div>
									<div className="col-md-6">
										<FormInput
											name="contract_end_date"
											control={control}
											label="Contract End Date"
											type="date"
											disabled={isSubmitting}
										/>
									</div>
								</div>

								{/* Lock Property Management */}
								<div className="mb-3">
									<Controller
										name="lock_property_management"
										control={control}
										render={({ field }) => (
											<div className="form-check">
												<input
													type="checkbox"
													className="form-check-input"
													id="lock-property-management"
													checked={field.value}
													onChange={field.onChange}
													disabled={isSubmitting}
												/>
												<label
													className="form-check-label"
													htmlFor="lock-property-management"
												>
													Lock Property Management
												</label>
												<small className="d-block text-muted">
													Prevent user from removing properties on their own
												</small>
											</div>
										)}
									/>
								</div>

								{/* Notes */}
								{false && (
									<FormInput
										name="notes"
										control={control}
										label="Notes"
										type="textarea"
										rows={3}
										placeholder="Internal notes about this subscription..."
										maxLength={2000}
										showCharCount
										disabled={isSubmitting}
									/>
								)}
							</>
						)}
					</>
				)}

				<div className="modal-footer">
					<button
						type="button"
						className="btn btn-secondary"
						onClick={handleClose}
						disabled={isSubmitting}
					>
						Cancel
					</button>
					<button
						type="submit"
						className="btn btn-primary"
						disabled={isSubmitting || isLoadingData}
					>
						{isSubmitting ? (
							<>
								<span
									className="spinner-border spinner-border-sm me-2"
									role="status"
								/>
								Creating...
							</>
						) : (
							<>
								<i className="bi bi-plus-lg me-2" />
								Create Subscription
							</>
						)}
					</button>
				</div>
			</form>
		</StandardModal>
	);
};

export default SubscriptionCreateModal;
